import { useIdvStatus } from "@faire/deprecated-retailer-visitor-shared/hooks/idv/useIdvStatus";
import OpenWithFaireApplicationButtonText from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplicationButtonText";
import { trackGoToIdvPage } from "@faire/retailer-visitor-shared/features/IDV/trackGoToIdvPage";
import { useCompleteStoreProfileUrl } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useCompleteStoreProfileUrl";
import { useOpenWithFaireApplicationStatus } from "@faire/retailer-visitor-shared/stores/domain/OpenWithFaire/useOpenWithFaireApplicationStatus";
import { useCreditSummary } from "@faire/retailer-visitor-shared/stores/domain/useCreditSummary";
import { IRetailerLaunchWithFaire } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaire";
import homepage from "@faire/web-api--source/routes/www/root";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { intlDate } from "@faire/web/common/localization/date-fns/intlDate";
import { formatMoney, moneyOrZero } from "@faire/web/common/money";
import { MoneyMath } from "@faire/web/common/MoneyMath";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import {
  StrictLocalMsg,
  chunkTransformations,
} from "@faire/web/ui/lib/localization";
import { SlideIn } from "@faire/web/ui/SlideIn/SlideIn";
import React from "react";
import styled from "styled-components";

export const ApplicationStatusText: React.FC = () => {
  const { appliedAt } = useOpenWithFaireApplicationStatus();

  if (!appliedAt) {
    return null;
  }

  return (
    <SlideIn offset="50px">
      <TextWrapper justify="center">
        <Typography variant="paragraphSansMedium">
          <StrictLocalMsg
            id="jmwmJP"
            defaultMessage="Your application on {appliedAtDate}"
            values={{ appliedAtDate: intlDate(appliedAt, "PPP") }}
            description={{
              text: "Faire has a retailer's application for a payment terms increase",
              img: "https://cdn.faire.com/fastly/12fc68a6df6bdc76a912e15fbec64f7a5eba35c1f33023117a04937b9090f7ef.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography
          data-test-id="owf-applied-message"
          variant={{
            mobileAndAbove: "displaySSerifRegular",
            tabletAndAbove: "displayMSerifRegular",
          }}
        >
          <ApplicationStatusTitle />
        </Typography>
        <Spacer height="2x" />
        <Typography>
          <ApplicationStatusDescription />
        </Typography>
        <ApplicationStatusCTA />
      </TextWrapper>
    </SlideIn>
  );
};

const ApplicationStatusTitle: React.FC = () => {
  const { hasPendingIdv, isPendingRiskAnalysis } = useIdvStatus();
  const isPendingIdv = hasPendingIdv === "yes" || isPendingRiskAnalysis;
  const { status } = useOpenWithFaireApplicationStatus();

  switch (status) {
    case IRetailerLaunchWithFaire.Status.REJECTED:
      return (
        <StrictLocalMsg
          id="rZh8bl"
          defaultMessage="We’re unable to offer you more payment terms at this time"
          description={{
            text: "Faire has rejected a retailer's application for a payment terms increase",
            img: "https://cdn.faire.com/fastly/2696b848b88945d40bd58aa1f490b06c59cdadeb513d54dfede686b4943ff96a.png",
          }}
        />
      );
    case IRetailerLaunchWithFaire.Status.ACCEPTED:
      return (
        <StrictLocalMsg
          id="jaOBzz"
          defaultMessage="Congrats! You’ve got more payment terms"
          description={{
            text: "Faire has accepted a retailer's application for a payment terms increase",
            img: "https://cdn.faire.com/fastly/0244328410970b0128eb47943aa4652e05adb3be2a9f956060c434d299c90076.png",
          }}
        />
      );
    case IRetailerLaunchWithFaire.Status.ON_HOLD:
    case IRetailerLaunchWithFaire.Status.ON_HOLD_NO_LEASE:
      if (isPendingIdv) {
        return <AppliedTitle />;
      }
      return (
        <StrictLocalMsg
          id="FGxrao"
          defaultMessage="Ready to finish up your application?"
        />
      );
    case IRetailerLaunchWithFaire.Status.APPLIED:
    case IRetailerLaunchWithFaire.Status.DELAYED_DECISION:
    default:
      return <AppliedTitle />;
  }
};

const ApplicationStatusDescription: React.FC = () => {
  const { hasPendingIdv, isPendingRiskAnalysis } = useIdvStatus();
  const isPendingIdv = hasPendingIdv === "yes" || isPendingRiskAnalysis;
  const { status } = useOpenWithFaireApplicationStatus();
  const { creditSummary } = useCreditSummary();
  const nextApplicationAllowedAt =
    creditSummary?.limit_increase?.next_application_allowed_at ?? 0;
  const availableTermsLimit = creditSummary?.available_credit;
  const availableTermsOrZero = moneyOrZero(availableTermsLimit);
  const formattedNextApplicationDate = intlDate(
    nextApplicationAllowedAt,
    "PPP"
  );

  switch (status) {
    case IRetailerLaunchWithFaire.Status.REJECTED:
      if (MoneyMath.greaterThanZero(availableTermsOrZero)) {
        return (
          <StrictLocalMsg
            id="J8MDvG"
            defaultMessage="Starting {nextApplicationDate}, you can reapply for an increase in 60-day payment terms. In the meantime, you still have immediate access to <StrongFormatting>{currentTermsMoney} in 60-day payment terms</StrongFormatting> to use on Faire."
            description={{
              text: "Faire has rejected a retailer's application for a payment terms increase",
              img: "https://cdn.faire.com/fastly/2696b848b88945d40bd58aa1f490b06c59cdadeb513d54dfede686b4943ff96a.png",
            }}
            values={{
              currentTermsMoney: formatMoney({
                money: availableTermsOrZero,
                omitCentsIfZero: true,
              }),
              nextApplicationDate: formattedNextApplicationDate,
              StrongFormatting: chunkTransformations.strong,
            }}
          />
        );
      }

      return (
        <StrictLocalMsg
          id="Qu7ZYU"
          defaultMessage="Starting {nextApplicationDate}, you can reapply for an increase in 60-day payment terms."
          description={{
            text: "Faire has rejected a retailer's application for a payment terms increase",
            img: "https://cdn.faire.com/fastly/3b2bfe8aab25c0e595bd80bb444ec68b8143c008788a57011929775e96062685.png",
          }}
          values={{
            nextApplicationDate: formattedNextApplicationDate,
          }}
        />
      );
    case IRetailerLaunchWithFaire.Status.ON_HOLD:
      if (isPendingIdv) {
        return <PendingIdvMessageDescription />;
      }
      return (
        <StrictLocalMsg
          id="bnPzwB"
          defaultMessage="We need some additional store details to finish processing your application."
          description={{
            text: "Description as part of Open with Faire banner",
            img: "https://cdn.faire.com/fastly/bda71c7e2dec40faba4072e5d3b5a2caef9621c0de4abe88e3b97dea46d95f5b.png",
          }}
        />
      );
    case IRetailerLaunchWithFaire.Status.ON_HOLD_NO_LEASE:
      if (isPendingIdv) {
        return <PendingIdvMessageDescription />;
      }
      return (
        <StrictLocalMsg
          id="Otv/hv"
          defaultMessage="We need your store lease info to process your application. Once you’ve secured a store space, let us know by completing your store profile."
          description={{
            text: "Description as part of Open with Faire banner",
            img: "https://cdn.faire.com/fastly/1580dbeb60ce8396bd06079a32d8c93993c90c99338bf199aadcbd371d5467c0.png",
          }}
        />
      );

    case IRetailerLaunchWithFaire.Status.APPLIED:
    case IRetailerLaunchWithFaire.Status.DELAYED_DECISION:
      if (isPendingIdv) {
        return <PendingIdvMessageDescription />;
      }
      return (
        <StrictLocalMsg
          id="WrbflS"
          defaultMessage="Thank you for applying! You’ll hear from us soon about your new 60 day payment terms."
          description={{
            text: "Faire has a retailer's application for a payment terms increase",
            img: "https://cdn.faire.com/fastly/d121cbb6ddbe9dee85c6525f85f5eb433041b7bbb8b1cbe49768d5eaf17fef1e.png",
          }}
        />
      );
    default:
      return null;
  }
};

const ApplicationStatusCTA: React.FC = () => {
  const { hasPendingIdv, isPendingRiskAnalysis } = useIdvStatus();
  const isPendingIdv = hasPendingIdv === "yes" || isPendingRiskAnalysis;
  const { status } = useOpenWithFaireApplicationStatus();
  const completeStoreProfileUrl = useCompleteStoreProfileUrl();
  const { isTabletAndAbove } = useViewport();

  switch (status) {
    case IRetailerLaunchWithFaire.Status.ACCEPTED:
      return (
        <>
          <Spacer size={isTabletAndAbove ? "3x" : "2x"} />
          <FitContentButton linkTo={homepage()}>
            <StrictLocalMsg
              id="hcPUH8"
              defaultMessage="Start buying"
              description={{
                text: "CTA to start buying on Faire",
              }}
            />
          </FitContentButton>
        </>
      );
    case IRetailerLaunchWithFaire.Status.ON_HOLD:
    case IRetailerLaunchWithFaire.Status.ON_HOLD_NO_LEASE:
      if (isPendingIdv) {
        return null;
      }
      return (
        <>
          <Spacer size={isTabletAndAbove ? "3x" : "2x"} />
          <FitContentButton
            linkTo={completeStoreProfileUrl}
            onClick={() => trackGoToIdvPage("OWF_ACADEMY_PAGE")}
          >
            <OpenWithFaireApplicationButtonText />
          </FitContentButton>
        </>
      );
    default:
      return null;
  }
};

const AppliedTitle: React.FC = () => {
  return (
    <StrictLocalMsg
      id="HDqiz6"
      defaultMessage="We’ve got your application for more payment terms"
      description={{
        text: "Faire has a retailer's application for a payment terms increase",
        img: "https://cdn.faire.com/fastly/12fc68a6df6bdc76a912e15fbec64f7a5eba35c1f33023117a04937b9090f7ef.png",
      }}
    />
  );
};

const PendingIdvMessageDescription: React.FC = () => {
  return (
    <StrictLocalMsg
      id="Cn47AA"
      defaultMessage="We’re in the process of verifying your account. Once your account is verified, you can complete your terms application."
      description={{
        text: "Faire is in the process of verifying your account. Once your account is verified, you can complete your terms application.",
        img: "https://cdn.faire.com/fastly/e57e4cb6427ba263b1b04b97e8d5ee7c3400ebbf52fabc0fde135e449f89c456.png",
      }}
    />
  );
};

const TextWrapper = styled(Column)`
  height: 100%;
  max-width: 528px;
`;

const FitContentButton = styled(Button)`
  width: fit-content;
`;
